/**
 * AddLanguageDialog Component
 * Modal dialog for adding or editing a language
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { AlertCircle, Image as ImageIcon, Pencil, Plus, Upload, X } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import { Language } from '../../types/admin';
import {
  LANGUAGE_FLAG_ACCEPT,
  formatFlagFileSize,
  validateLanguageFlagFile,
} from './languageFlagFile';

// ============================================
// Props Interface
// ============================================

interface AddLanguageDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (language: Language, flagFile?: File) => Promise<void> | void;
  existingKeys: string[];
  initialLanguage?: Language | null;
  isSaving?: boolean;
}

// ============================================
// AddLanguageDialog Component
// ============================================

export const AddLanguageDialog: React.FC<AddLanguageDialogProps> = ({
  open,
  onClose,
  onSave,
  existingKeys,
  initialLanguage,
  isSaving = false,
}) => {
  const [key, setKey] = useState('');
  const [aliasesInput, setAliasesInput] = useState('');
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [flagFile, setFlagFile] = useState<File | undefined>();
  const [flagPreview, setFlagPreview] = useState('');
  const [flagError, setFlagError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notification = useNotification();
  const isEditMode = Boolean(initialLanguage);

  const resetFlagFile = () => {
    setFlagFile(undefined);
    setFlagError('');
    setIsDragging(false);
    setFlagPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }

      return '';
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setKey('');
    setAliasesInput('');
    setName('');
    setActive(true);
    resetFlagFile();
    onClose();
  };

  useEffect(() => {
    if (open && initialLanguage) {
      setKey(initialLanguage.key);
      setAliasesInput((initialLanguage.aliases ?? []).join(', '));
      setName(initialLanguage.name);
      setActive(initialLanguage.active);
      resetFlagFile();
      return;
    }

    if (open) {
      setKey('');
      setAliasesInput('');
      setName('');
      setActive(true);
      resetFlagFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLanguage]);

  const applyFlagFile = (file: File) => {
    const error = validateLanguageFlagFile(file);
    if (error) {
      setFlagError(error);
      return;
    }

    setFlagError('');
    setFlagFile(file);
    setFlagPreview((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }

      return URL.createObjectURL(file);
    });
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      applyFlagFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSave = async () => {
    if (!key.trim()) {
      notification.error('Language key is required');
      return;
    }

    if (!name.trim()) {
      notification.error('Language name is required');
      return;
    }

    const keyRegex = /^[a-z]{2,3}$/;
    if (!keyRegex.test(key)) {
      notification.error('Language key must be 2-3 lowercase letters (e.g., en, vi, ja)');
      return;
    }

    const normalizedKey = key.toLowerCase();
    const aliases = aliasesInput
      .split(',')
      .map((alias) => alias.trim().toLowerCase())
      .filter(Boolean);
    const aliasRegex = /^[a-z]{2,8}(-[a-z0-9]{2,8})?$/;
    const invalidAlias = aliases.find((alias) => !aliasRegex.test(alias));

    if (invalidAlias) {
      notification.error(`Additional key "${invalidAlias}" is not valid`);
      return;
    }

    if (aliases.includes(normalizedKey)) {
      notification.error('Additional keys must be different from the canonical key');
      return;
    }

    if (new Set(aliases).size !== aliases.length) {
      notification.error('Duplicate additional keys are not allowed');
      return;
    }

    const currentIdentifiers = new Set([initialLanguage?.key, ...(initialLanguage?.aliases ?? [])]);
    const conflictingIdentifier = [normalizedKey, ...aliases].find(
      (identifier) => existingKeys.includes(identifier) && !currentIdentifiers.has(identifier)
    );

    if (conflictingIdentifier) {
      notification.error(`Language key "${conflictingIdentifier}" already exists`);
      return;
    }

    try {
      await onSave(
        {
          key: normalizedKey,
          aliases,
          name: name.trim(),
          active,
          flagUrl: initialLanguage?.flagUrl,
        },
        flagFile
      );
      handleClose();
    } catch {
      // Parent owns the user-facing failure notification.
    }
  };

  const previewSrc = flagPreview || initialLanguage?.flagUrl || '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle>
        <Box className="flex items-center gap-2">
          <Plus size={24} className="text-blue-500" />
          <Typography variant="h6" className="font-semibold">
            {isEditMode ? 'Edit Language' : 'Add New Language'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className="space-y-4 pt-2">
          <TextField
            label="Language Key"
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase())}
            placeholder="e.g., en, vi, ja"
            fullWidth
            helperText="2-3 lowercase letters (ISO 639-1 code)"
            disabled={isEditMode || isSaving}
            inputProps={{
              maxLength: 3,
              style: { fontFamily: 'monospace' },
            }}
          />

          <TextField
            label="Additional Keys"
            value={aliasesInput}
            onChange={(e) => setAliasesInput(e.target.value.toLowerCase())}
            placeholder="e.g., vn, vi-vn"
            fullWidth
            disabled={isSaving}
            helperText="Comma-separated accepted keys mapped to this language"
            inputProps={{
              style: { fontFamily: 'monospace' },
            }}
          />

          <TextField
            label="Language Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., English, Tiếng Việt, 日本語"
            fullWidth
            disabled={isSaving}
            helperText="Display name in the native language"
          />

          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(event) => setActive(event.target.checked)}
                disabled={isSaving}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#3B82F6',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#3B82F6',
                  },
                }}
              />
            }
            label="Active"
          />

          <Box>
            <Typography variant="subtitle2" className="mb-2 font-semibold text-gray-800">
              Flag icon
            </Typography>
            {!previewSrc ? (
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !isSaving && fileInputRef.current?.click()}
                className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors"
                sx={{
                  borderColor: isDragging ? '#3B82F6' : flagError ? '#EF4444' : '#cbd5e1',
                  backgroundColor: isDragging ? 'rgba(59, 130, 246, 0.05)' : '#f8fafc',
                  '&:hover': {
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                  },
                }}
              >
                <Upload size={28} className="mb-2 text-blue-400" />
                <Typography variant="body2" className="font-medium text-gray-700">
                  Drag & drop or click to upload
                </Typography>
                <Typography variant="caption" className="text-gray-500">
                  PNG, JPEG, WebP, SVG · Max 2 MB
                </Typography>
              </Box>
            ) : (
              <Box className="overflow-hidden rounded-lg border border-slate-200">
                <Box className="flex h-28 items-center justify-center bg-slate-50 p-4">
                  <img
                    src={previewSrc}
                    alt="Flag preview"
                    className="max-h-full max-w-full object-contain"
                  />
                </Box>
                <Box className="flex items-center justify-between px-3 py-2">
                  <Box className="flex min-w-0 items-center gap-2">
                    <ImageIcon size={16} className="shrink-0 text-blue-400" />
                    <Typography variant="caption" className="truncate text-gray-600">
                      {flagFile ? flagFile.name : `${name || initialLanguage?.name} flag`}
                    </Typography>
                    {flagFile && (
                      <Typography variant="caption" className="shrink-0 text-gray-400">
                        {formatFlagFileSize(flagFile.size)}
                      </Typography>
                    )}
                  </Box>
                  <Box className="flex items-center gap-1">
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSaving}
                    >
                      <Upload size={14} />
                    </IconButton>
                    {flagFile && (
                      <IconButton size="small" onClick={resetFlagFile} disabled={isSaving}>
                        <X size={14} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            {flagError && (
              <Box className="mt-2 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2">
                <AlertCircle size={16} className="text-red-500" />
                <Typography variant="caption" className="text-red-600">
                  {flagError}
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={LANGUAGE_FLAG_ACCEPT}
              className="hidden"
              disabled={isSaving}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  applyFlagFile(file);
                }

                if (event.target) {
                  event.target.value = '';
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          startIcon={
            isSaving ? (
              <CircularProgress size={18} color="inherit" />
            ) : isEditMode ? (
              <Pencil size={18} />
            ) : (
              <Plus size={18} />
            )
          }
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Save Language' : 'Add Language'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
